<div class="inventions-view" [class.is-popup]="isPopup">
  <header class="page-header" *ngIf="!isPopup">
    <div class="page-title-row">
      <h4 class="page-title">{{ tab_name }}</h4>
      <button *ngIf="query" (click)="toggleQueryBox()" class="eye-btn" type="button">
        <i class="bi"
          [matTooltip]="showQueryBox ? 'Hide Query' : 'View Query'"
          [ngClass]="showQueryBox ? 'bi-eye-slash' : 'bi-eye'"
          matTooltipPosition="above"></i>
      </button>
    </div>

    <p class="page-subtitle" *ngIf="(tabs$ | async)?.search_type === 'company'">
      Displaying up to 100 of the most recent inventions (patent families) owned by {{ tab_name }}.
      Adjust the end date or quality score to refine results. If fewer than 100 match, all available are shown.
    </p>
    <p class="page-subtitle" *ngIf="(tabs$ | async)?.search_type === 'theme'">
      Displaying up to 100 of the most recent inventions (patent families) in this technology area.
      Adjust the end date or quality score to refine results. If fewer than 100 match, all available are shown.
    </p>

    <div *ngIf="showQueryBox && query" class="query-alert" role="alert">{{ query }}</div>
  </header>

  <section class="chart-card">
    <div class="controls-row">
      <button class="ai-generate-button"
        (click)="generateSummary()"
        [disabled]="loadingSummary || showSpinner || inventions_data.length === 0">
        <ng-container *ngIf="!loadingSummary">
          <i class="bi bi-stars"></i> Generate AI Summary
        </ng-container>
        <ng-container *ngIf="loadingSummary">
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Generating...
        </ng-container>
      </button>

      <div class="filters" [formGroup]="form">
        <label class="filter-field">
          <span class="filter-label">End Date</span>
          <input class="filter-input" type="date" formControlName="date_end" [max]="getToday()">
        </label>

        <label class="filter-field">
          <span class="filter-label">
            Quality Filter
            <i class="bi bi-info-circle"
              [matTooltip]="'Inventions scoring higher than 97 are considered High Quality Inventions.'"
              matTooltipPosition="above"></i>
          </span>
          <div class="dropdown">
            <button class="filter-input filter-dropdown-toggle" type="button" data-bs-toggle="dropdown">
              {{ form.get('quality_filter')?.value | titlecase }}
              <i class="bi bi-chevron-down"></i>
            </button>
            <ul class="dropdown-menu">
              <li><button class="dropdown-item" (click)="form.get('quality_filter')?.setValue('all')">All</button></li>
              <li><button class="dropdown-item" (click)="form.get('quality_filter')?.setValue('high')">High Quality</button></li>
            </ul>
          </div>
        </label>

        <button type="submit" class="primary-btn" (click)="onSubmit()">Submit</button>

        <button class="action-btn" (click)="downloadExcel()" matTooltip="Download Excel">
          <i class="bi bi-download"></i>
        </button>
      </div>
    </div>

    <div *ngIf="summaryText" class="ai-summary-card">
      <p>{{ summaryText }}</p>
    </div>

    <div *ngIf="showSpinner" class="text-center py-4">
      <div class="spinner-border text-secondary" role="status"></div>
    </div>

    <div *ngIf="noInventionsFound && !showSpinner" class="empty-state">
      <div class="empty-state-icon"><i class="bi bi-search"></i></div>
      <h5>No inventions found</h5>
      <p>No inventions match the current filters. Try adjusting the end date or quality filter.</p>
    </div>

    <div *ngIf="inventions_data_loading_failed && !showSpinner" class="empty-state">
      <div class="empty-state-icon"><i class="bi bi-exclamation-circle"></i></div>
      <h5>Unable to load data</h5>
      <p>Please try again later, or contact <a href="mailto:info@quant-ip.com">info&#64;quant-ip.com</a>.</p>
    </div>

    <div *ngIf="showResults && inventions_data.length > 0" class="table-wrap">
      <table class="table">
        <thead>
          <tr>
            <th style="width:3%"></th>
            <th style="width:40%">Invention Title</th>
            <th style="width:10%">Date of Publication
              <i class="bi bi-info-circle"
                [matTooltip]="'Earliest publication date in the patent family.'"
                matTooltipPosition="above"></i>
            </th>
            <th style="width:23%">Filing Entities
              <i class="bi bi-info-circle"
                [matTooltip]="'Companies named as applicants on the patent filing.'"
                matTooltipPosition="above"></i>
            </th>
            <th style="width:13%">Primary Cluster
              <i class="bi bi-info-circle"
                [matTooltip]="'Quant IP technology cluster that best represents the patent family, derived from its patent classifications.'"
                matTooltipPosition="above"></i>
            </th>
            <th style="width:7%">Quality Score
              <i class="bi bi-info-circle"
                [matTooltip]="'Proprietary quality indicator based on grant robustness, market coverage and technological relevance. Each invention receives a score between 0-100, relative to similar inventions.'"
                matTooltipPosition="above"></i>
            </th>
            <th style="width:4%">Link</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let invention of pagedInventions">
            <td>
              <i class="bi bi-copy copy-icon"
                matTooltip="Copy family Id of the invention"
                [cdkCopyToClipboard]="invention.family_id"></i>
            </td>
            <td class="cell-title">
              <span class="truncate-tooltip" [matTooltip]="invention.invention_title">
                {{ invention.invention_title | titlecase }}
              </span>
            </td>
            <td>{{ invention.date_publication | date: 'dd/MM/yyyy' }}</td>
            <td>
              <div class="truncate-multiline-tooltip" [title]="invention.applicants_filing">
                <ng-container *ngFor="let name of invention.applicants_filing">
                  {{ name.trim() }}<br>
                </ng-container>
              </div>
            </td>
            <td>{{ invention['cpc_cluster.label'] }}</td>
            <td>
              <span class="quality-badge">{{ invention.quality_score | nullableNumber: '1.1-1' }}</span>
            </td>
            <td>
              <a [href]="invention.link_espacenet || null"
                [attr.target]="invention.link_espacenet ? '_blank' : null"
                [matTooltip]="invention.link_espacenet ? 'Click to view the patent in espacenet' : 'No link available'"
                [style.pointer-events]="invention.link_espacenet ? 'auto' : 'none'"
                [style.opacity]="invention.link_espacenet ? '1' : '0.4'"
                class="link-icon">
                <i class="bi bi-link-45deg"></i>
              </a>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="paginator" *ngIf="totalPages > 1">
        <button class="page-btn" type="button"
          [disabled]="currentPage === 0"
          (click)="goToPage(currentPage - 1)">
          <i class="bi bi-chevron-left"></i>
        </button>
        <button *ngFor="let p of pageNumbers"
          class="page-btn" type="button"
          [class.active]="p === currentPage"
          (click)="goToPage(p)">
          {{ p + 1 }}
        </button>
        <button class="page-btn" type="button"
          [disabled]="currentPage === totalPages - 1"
          (click)="goToPage(currentPage + 1)">
          <i class="bi bi-chevron-right"></i>
        </button>
        <span class="page-info">{{ inventions_data.length }} inventions</span>
      </div>
    </div>
  </section>
</div>